import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { contactsApi } from './api'
import { handleApiError } from '@/lib/api/axios'
import { useAuthStore } from '@/app/store/auth'
import type { Contact } from '@/types/entities'
import type { ListParams } from '@/lib/api/types'

// Query keys
export const contactKeys = {
    all: ['contacts'] as const,
    lists: () => [...contactKeys.all, 'list'] as const,
    list: (params: ListParams) => [...contactKeys.lists(), params] as const,
    details: () => [...contactKeys.all, 'detail'] as const,
    detail: (id: number) => [...contactKeys.details(), id] as const,
}

// Contacts hooks
export function useContacts(params?: ListParams) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: contactKeys.list(params || {}),
        queryFn: () => contactsApi.list(params),
        enabled: isAuthenticated && !mfaRequired,
        placeholderData: keepPreviousData,
    })
}

export function useContact(id: number) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: contactKeys.detail(id),
        queryFn: () => contactsApi.get(id),
        select: (response) => response.data,
        enabled: !!id && isAuthenticated && !mfaRequired,
    })
}

export function useCreateContact() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (data: Omit<Contact, 'id'>) =>
            contactsApi.create(data),
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: contactKeys.lists() })
            const name = `${response.data.firstName || ''} ${response.data.surname || ''}`.trim() || 'Contact'
            toast({
                title: 'Contact Created',
                description: `${name} has been created successfully.`,
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

export function useUpdateContact() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: Partial<Omit<Contact, 'id'>> }) =>
            contactsApi.update(id, data),
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({ queryKey: contactKeys.lists() })
            queryClient.invalidateQueries({ queryKey: contactKeys.detail(variables.id) })
            const name = `${response.data.firstName || ''} ${response.data.surname || ''}`.trim() || 'Contact'
            toast({
                title: 'Contact Updated',
                description: `${name} has been updated successfully.`,
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

export function useDeleteContact() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (id: number) => contactsApi.delete(id),
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: contactKeys.lists() })
            queryClient.removeQueries({ queryKey: contactKeys.detail(id) })
            toast({
                title: 'Contact Deleted',
                description: 'Contact has been deleted successfully.',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}
